import { zodResolver } from '@hookform/resolvers/zod';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { DETAIL_LABEL_CLASS } from '@/components/layout/DetailField';
import { cn } from '@/shared/lib/cn';

import { personalDetailsSchema, type PersonalDetailsInput } from './account.schema';

interface DirectorIdFieldProps {
  /** The saved value. Absent while ABRS has not issued the director one. */
  directorId: string | null | undefined;
  /** Resolves true when the value was saved; false leaves the editor open. */
  onSave: (din: string) => Promise<boolean>;
  isSaving: boolean;
}

/**
 * The one writable value on the Personal Details card.
 *
 * The card as a whole is no longer a form: ASIC owns everything else and an
 * agent changes it, so a card-wide Edit mode put three permanently disabled
 * inputs on screen to reach one editable field. Editing lives on the field
 * instead — the rest of the card never changes shape.
 *
 * Remounting on cancel is what discards an abandoned edit, so there is no
 * reset call to keep in step with the saved value.
 */
export const DirectorIdField = ({ directorId, onSave, isSaving }: DirectorIdFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="min-w-0">
      {/* The pen sits on the label row, immediately after the label — not
          pushed to the card's edge, where it reads as acting on the whole card
          rather than this one field. Keeping it off the value row means a long
          DIN never has to wrap around a 44px target at 360px. */}
      <div className="flex min-w-0 items-center gap-1">
        <Typography
          variant="caption"
          component="dt"
          color="text.secondary"
          className={cn('min-w-0 truncate', DETAIL_LABEL_CLASS)}
        >
          Director ID
        </Typography>
        {!isEditing && (
          <IconButton
            size="small"
            aria-label="Edit Director ID"
            onClick={() => {
              setIsEditing(true);
            }}
            className="-my-2 shrink-0"
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <dd className="mt-1 min-w-0">
        {isEditing ? (
          <DirectorIdEditor
            key={directorId ?? ''}
            directorId={directorId}
            isSaving={isSaving}
            onCancel={() => {
              setIsEditing(false);
            }}
            onSave={async (din) => {
              if (await onSave(din)) {
                setIsEditing(false);
              }
            }}
          />
        ) : (
          <Typography className="text-[15px] leading-snug break-words">
            {directorId !== null && directorId !== undefined && directorId !== ''
              ? directorId
              : '—'}
          </Typography>
        )}
      </dd>
    </div>
  );
};

interface DirectorIdEditorProps {
  directorId: string | null | undefined;
  onSave: (din: string) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

const DirectorIdEditor = ({
  directorId,
  onSave,
  onCancel,
  isSaving,
}: DirectorIdEditorProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<PersonalDetailsInput>({
    resolver: zodResolver(personalDetailsSchema),
    mode: 'onTouched',
    defaultValues: { din: directorId ?? '' },
  });

  const submit = handleSubmit(async ({ din }) => {
    await onSave(din);
  });

  return (
    <form
      onSubmit={(event) => void submit(event)}
      noValidate
      className="flex items-start gap-1"
    >
      <TextField
        size="small"
        autoFocus
        fullWidth
        // A DIN is 15 digits: the numeric keypad is the only correct
        // keyboard, and autoComplete must not offer an unrelated saved value.
        inputMode="numeric"
        autoComplete="off"
        error={errors.din !== undefined}
        helperText={errors.din?.message ?? '15 digits, issued by ABRS'}
        // The `dt` above already labels this field on screen; the input still
        // needs its own accessible name, and `aria-label` on the TextField
        // itself would land on the wrapper rather than the input.
        slotProps={{ htmlInput: { 'aria-label': 'Director ID' } }}
        {...register('din')}
      />
      <IconButton
        type="submit"
        size="small"
        color="primary"
        aria-label="Save Director ID"
        disabled={!isDirty || isSaving}
      >
        <CheckIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Cancel editing Director ID"
        onClick={onCancel}
        disabled={isSaving}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </form>
  );
};
